import { AxiosInstance } from "axios";
import { CheckedOutStatus } from "../constants/checkedOutStatus";
import { IObjectUpdate } from "../../rematch/interfaces/IObjectUpdate";
import IMFilesPropertyDefinition from "../interfaces/mfiles/IMFilesPropertyDefinition";
import { IPropertyValue } from "../interfaces/mfiles/IObjectUpdateInfo";
import { fetchObjectPropertiesUrl, updateCheckedOutStatusUrl, rollbackChangesUrl } from "../helpers/urlHelper";
import { createMfilesProperty } from "../helpers/propertyHelpers";
import { IMfilesObject as IMFilesObject } from "../interfaces/mfiles/IMFilesObject";
import { IItem } from "../interfaces/IItem";
import { IConfiguration } from "../interfaces/configuration/IConfiguration";
import { fetchSpecificItemPropertiesAsync } from "./FetchItemsService";

const RETRY_COUNT = 3;

// Updates properties of many objects in M-files.
// Updates are batched, because changes often need to occur on an all-or-nothing basis.
// I.e. a parent task is moved and all of its children move as well
const updateManyObjectsAsync = async (
  httpClient: AxiosInstance,
  configuration: IConfiguration,
  updates: Array<IObjectUpdate>
): Promise<IItem[]> => {
  // 1. Checkout objects
  try {
    for (const u of updates) {
      await checkoutObjectAsync(httpClient, u.objectType, u.objectId);
    }
  } catch (error) {
    console.log("Error while trying to log out items");
    if (error.response?.data?.ErrorCode === "25") {
      console.log("Item logged out to another user");
    } 
    // Something went wrong. Some of the objects are checked out by other users etc. Let's undo checkouts.
    for (const u of updates) {
      await checkinObjectAsyncWithRetries(httpClient, u.objectType, u.objectId, RETRY_COUNT);
    }
    throw error;
  }

  // 2. Update properties
  try {
    for (const u of updates) {
      await updateObjectPropertiesAsyncWithRetries(httpClient, u.objectType, u.objectId, mapIObjectUpdateInfo(configuration.propertyDefinitions, u), RETRY_COUNT);
    }
  } catch (error) {
    console.log("Error while trying to update data. Attempting rollback...");

    // Something went wrong with the update. Rollback and undo checkout
    for (const u of updates) {
      await rollBackPendingChangesAsyncWithRetries(httpClient, u.objectType, u.objectId, RETRY_COUNT);
    }
    for (const u of updates) {
      await checkinObjectAsyncWithRetries(httpClient, u.objectType, u.objectId, RETRY_COUNT);
    }
    throw error;
  }

  const updatedItems: IItem[] = [];

  // 3. Checkin objects
  for (const u of updates) {
    const checkedinItem = await checkinObjectAsyncWithRetries(httpClient, u.objectType, u.objectId, RETRY_COUNT);
    const objectTypeConfig = configuration.objectTypeConfigurations.get(u.objectType);
    const itemWithProperties = await fetchSpecificItemPropertiesAsync(httpClient, objectTypeConfig, checkedinItem);
    itemWithProperties.id = u.id;
    updatedItems.push(itemWithProperties);
  }

  return updatedItems;
};

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const checkinObjectAsyncWithRetries = async (httpClient: AxiosInstance, objectType: number, objectId: number, retryCount: number): Promise<IMFilesObject> => {
  let count = 0;
  let lastError = null;
  while(count < retryCount) {
    try {
      await sleep(count * 500);
      return await checkinObjectAsync(httpClient, objectType, objectId);
    } catch (error) {
      count++;
      lastError = error;
    }
  }
  throw lastError;
} 

const rollBackPendingChangesAsyncWithRetries = async (httpClient: AxiosInstance, objectType: number, objectId: number, retryCount: number) => {
  let count = 0;
  let lastError = null;
  while(count < retryCount) {
    try {
      await sleep(count * 500);
      return await rollBackPendingChangesAsync(httpClient, objectType, objectId);
    } catch (error) {
      count++;
      lastError = error;
    }
  }
  throw lastError;
} 

const updateObjectPropertiesAsyncWithRetries = async (httpClient: AxiosInstance, objectType: number, objectId: number, propertyValues: Array<IPropertyValue>, retryCount: number) => {
  let count = 0;
  let lastError = null;
  while(count < retryCount) {
    try {
      await sleep(count * 500);
      return await updateObjectPropertiesAsync(httpClient, objectType, objectId, propertyValues);
    } catch (error) {
      count++;
      lastError = error;
    }
  }
  throw lastError;
} 

const checkoutObjectAsync = async (httpClient: AxiosInstance, objectType: number, objectId: number): Promise<IMFilesObject> => {
  const checkoutResponse = await httpClient.put(updateCheckedOutStatusUrl(objectType, objectId), { Value: CheckedOutStatus.CheckedOutToCurrentUser });
  const mfilesObject: IMFilesObject = checkoutResponse.data;
  return mfilesObject;
};

const checkinObjectAsync = async (httpClient: AxiosInstance, objectType: number, objectId: number): Promise<IMFilesObject> => {
  const checkinResponse = await httpClient.put(updateCheckedOutStatusUrl(objectType, objectId), { Value: CheckedOutStatus.CheckedIn });
  const mfilesObject: IMFilesObject = checkinResponse.data;
  return mfilesObject;
};

const rollBackPendingChangesAsync = async (httpClient: AxiosInstance, objectType: number, objectId: number) => {
  await httpClient.delete(rollbackChangesUrl(objectType, objectId));
};

const updateObjectPropertiesAsync = async (
  httpClient: AxiosInstance,
  objectType: number,
  objectId: number,
  propertyValues: Array<IPropertyValue>
) => {
  const url = fetchObjectPropertiesUrl(objectType, objectId);
  await httpClient.post(url, propertyValues);
};

const mapIObjectUpdateInfo = (
  propertyDefinitions: Map<number, IMFilesPropertyDefinition>,
  update: IObjectUpdate
): Array<IPropertyValue> => {
  return update.mfPropertyUpdates.map((p) => {
    const propDef = propertyDefinitions.get(p.propertyId);
    return createMfilesProperty(propDef?.ID, propDef?.DataType, p.newValue);
  });
};

export { updateManyObjectsAsync };
